import { useEffect, useState, useMemo } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  UserCircleIcon, MoonIcon, PhoneCallIcon, EnvelopeIcon, WhatsappLogoIcon,
  ArrowRightIcon, UsersThreeIcon, MapPinIcon, CalendarIcon, WarningIcon,
} from "@phosphor-icons/react";

import { useLiveSearch, SearchBox } from "@/components/LiveSearch";
/**
 * Clientes Dormidos — gestión de reactivación (Módulo 8.2 del Excel spec).
 * Lista clientes sin compra en X días (configurable) con acciones directas:
 *  - Llamar (tel:)
 *  - WhatsApp (wa.me/)
 *  - Email (mailto:)
 *  - Ver ficha del cliente
 *
 * Resumen por RTC para priorizar campañas de reactivación.
 */
const QUICK_DAYS = [30, 60, 90, 180, 365];

export default function DormantClients() {
  const navigate = useNavigate();
  const [days, setDays] = useState(60);
  const [includeNever, setIncludeNever] = useState(true);
  const [data, setData] = useState({ items: [], total: 0, resumen_por_rtc: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = usePageState("search", "");
  const [regionFilter, setRegionFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/clients/dormant", {
        params: { days, include_never: includeNever },
      });
      setData(d);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [days, includeNever]);

  const porRegion = useMemo(() => (data.items || []).filter((c) => !regionFilter || c.region === regionFilter), [data.items, regionFilter]);
  const busqueda = useLiveSearch(porRegion, (c) => [c.nombre, c.codigo, c.codigo_cliente, c.rtn, c.rtc_nombre, c.telefono, c.region], { estado: [search, setSearch] });
  const filtered = busqueda.resultados;

  // Regiones únicas para el filtro
  const regiones = [...new Set(data.items.map((c) => c.region).filter(Boolean))].sort();

  const totalPerdido = data.resumen_por_rtc.reduce((s, r) => s + (r.total_perdido || 0), 0);

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="dormant-clients-page">
        {/* HEADER */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MoonIcon size={22} weight="duotone" className="text-blue-400" />
              <h1 className="text-2xl font-heading font-bold">Clientes Dormidos</h1>
            </div>
            <p className="text-sm text-text-muted">Reactivación: clientes que NO compran hace tiempo y necesitan seguimiento.</p>
          </div>
        </div>

        {/* CONFIG: días + incluir nunca-compró */}
        <div className="bg-card border border-border/60 rounded-lg p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs uppercase tracking-wider text-text-muted">Sin compra hace:</label>
            <div className="flex flex-wrap gap-2">
              {QUICK_DAYS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  data-testid={`btn-days-${d}`}
                  className={`px-3 py-1.5 text-xs rounded-md border ${
                    days === d ? "bg-blue-500/20 text-blue-400 border-blue-500/40" :
                    "border-border text-text-secondary hover:border-blue-500/40"
                  }`}
                >
                  +{d} días
                </button>
              ))}
              <input
                type="number"
                value={days}
                min={1}
                max={730}
                onChange={(e) => setDays(parseInt(e.target.value, 10) || 60)}
                data-testid="days-input"
                className="w-24 bg-app border border-border rounded-md px-3 py-1.5 text-xs focus:border-blue-500"
              />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer ml-auto">
              <input
                type="checkbox"
                checked={includeNever}
                onChange={(e) => setIncludeNever(e.target.checked)}
                data-testid="include-never-check"
                className="accent-blue-500"
              />
              Incluir clientes que <strong>nunca</strong> compraron
            </label>
          </div>
        </div>

        {/* MÉTRICAS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Clientes dormidos" value={data.total} icon={UserCircleIcon} color="text-blue-400" />
          <KPI label={`Umbral (días)`} value={data.umbral_dias || days} icon={CalendarIcon} color="text-agro-yellow" />
          <KPI label="Histórico potencial" value={fmtL(totalPerdido)} icon={WarningIcon} color="text-red-400" />
          <KPI label="RTCs afectados" value={data.resumen_por_rtc?.length || 0} icon={UsersThreeIcon} color="text-agro-green" />
        </div>

        {/* RESUMEN POR RTC */}
        {data.resumen_por_rtc?.length > 0 && (
          <div className="bg-card border border-border/60 rounded-lg p-4">
            <h3 className="text-xs uppercase tracking-wider text-text-muted mb-2">Resumen por RTC (priorizar reactivación)</h3>
            <div className="flex flex-wrap gap-2">
              {data.resumen_por_rtc.map((r) => (
                <div key={r.rtc_nombre} className="px-3 py-2 bg-app rounded-md border border-border/60 text-xs">
                  <span className="font-medium">{r.rtc_nombre}</span>
                  <span className="text-blue-400 ml-2">{r.count}</span>
                  {r.total_perdido > 0 && <span className="text-text-muted ml-2">· {fmtL(r.total_perdido)}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FILTROS */}
        <div className="flex flex-wrap gap-2">
          <SearchBox busqueda={busqueda} placeholder="Buscar nombre, código, RTN, RTC o teléfono…" testid="dormant-search" label="Buscar clientes inactivos" />
          {regiones.length > 0 && (
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="bg-card border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500"
            >
              <option value="">Todas las regiones</option>
              {regiones.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
        </div>

        {/* TABLA */}
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">Cliente</th>
                <th className="p-3">RTC asignado</th>
                <th className="p-3">Región</th>
                <th className="p-3">Última compra</th>
                <th className="p-3 text-right">Días dormido</th>
                <th className="p-3 text-right">Hist. facturas</th>
                <th className="p-3 pr-5 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="p-10 text-center text-text-muted">Cargando…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="7" className="p-10 text-center text-text-muted">
                  No hay clientes dormidos con esos criterios. ¡Buenas ventas!
                </td></tr>
              ) : filtered.map((c) => {
                const sevColor = c.nunca_compro ? "text-text-muted" :
                                 c.dias_dormido > 365 ? "text-red-400" :
                                 c.dias_dormido > 180 ? "text-orange-400" :
                                 c.dias_dormido > 90 ? "text-agro-yellow" : "text-blue-400";
                const telDigits = (c.telefono || "").replace(/\D/g, "");
                return (
                  <tr key={c.id} className="border-t border-border/40 hover:bg-app/40" data-testid={`dormant-row-${c.id}`}>
                    <td className="p-3 pl-5">
                      <button
                        onClick={() => navigate(`/clientes/${c.id}`)}
                        className="text-left hover:text-blue-400"
                      >
                        <div className="font-medium">{c.nombre}</div>
                        <div className="text-[10px] text-text-muted">{c.codigo}</div>
                      </button>
                    </td>
                    <td className="p-3 text-xs">{c.rtc_nombre || <span className="text-text-muted italic">sin asignar</span>}</td>
                    <td className="p-3 text-xs">
                      {c.region ? <span className="inline-flex items-center gap-1"><MapPinIcon size={11} />{c.region}</span> : <span className="text-text-muted">—</span>}
                    </td>
                    <td className="p-3 text-xs">
                      {c.nunca_compro ? (
                        <span className="text-text-muted italic">Nunca compró</span>
                      ) : (
                        c.ultima_compra?.slice(0, 10)
                      )}
                    </td>
                    <td className={`p-3 text-right font-medium ${sevColor}`}>
                      {c.nunca_compro ? "—" : c.dias_dormido}
                    </td>
                    <td className="p-3 text-right text-xs">{c.total_facturas || 0}</td>
                    <td className="p-3 pr-5">
                      <div className="flex justify-center gap-1">
                        {telDigits && (
                          <>
                            <a
                              href={`tel:${telDigits}`}
                              title="Llamar"
                              data-testid={`call-${c.id}`}
                              className="p-1.5 rounded text-text-muted hover:text-blue-400 hover:bg-blue-500/10"
                            >
                              <PhoneCallIcon size={14} />
                            </a>
                            <a
                              href={`https://wa.me/${telDigits}?text=${encodeURIComponent(`Hola ${c.nombre}, te contactamos de AGROPORT...`)}`}
                              target="_blank"
                              rel="noreferrer"
                              title="WhatsApp"
                              data-testid={`whatsapp-${c.id}`}
                              className="p-1.5 rounded text-text-muted hover:text-agro-green hover:bg-agro-green/10"
                            >
                              <WhatsappLogoIcon size={14} weight="bold" />
                            </a>
                          </>
                        )}
                        {c.email && (
                          <a
                            href={`mailto:${c.email}?subject=${encodeURIComponent("Te extrañamos en AGROPORT")}`}
                            title="Email"
                            data-testid={`email-${c.id}`}
                            className="p-1.5 rounded text-text-muted hover:text-agro-yellow hover:bg-agro-yellow/10"
                          >
                            <EnvelopeIcon size={14} />
                          </a>
                        )}
                        <button
                          onClick={() => navigate(`/clientes/${c.id}`)}
                          title="Ver ficha"
                          data-testid={`view-${c.id}`}
                          className="p-1.5 rounded text-text-muted hover:text-text-primary hover:bg-white/5"
                        >
                          <ArrowRightIcon size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* HELP */}
        <div className="bg-card border border-border/30 rounded-lg p-4 text-xs text-text-muted">
          <strong>Cómo usar este módulo:</strong>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Ajusta el umbral de días según tu ciclo comercial (recomendado: 60-90 días).</li>
            <li>Filtra por RTC para priorizar el seguimiento por vendedor responsable.</li>
            <li>Las acciones rápidas (llamar/WhatsApp/email) abren la app correspondiente del dispositivo.</li>
            <li>Click en el nombre del cliente abre su ficha completa para ver historial e información detallada.</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}

function KPI({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
          <div className={`text-xl font-bold mt-1 ${color}`}>{value}</div>
        </div>
        <Icon size={20} weight="duotone" className={color} />
      </div>
    </div>
  );
}
